import { useMemo, ReactElement, PropsWithChildren, useCallback } from "react";

import {
  Stack,
  Text,
  HStack,
  Switch,
  Spacer,
  Checkbox,
  Button,
  Skeleton,
} from "@chakra-ui/react";

import DatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";

import { useRequestParams } from "contexts/RequestParamsProvider";
import PrimaryButton from "components/PrimaryButton";
import { mobileStyleBreakpoint } from "util/styleHelpers";
import Card, { CardBody, CardHeader, CardProps } from "./Card";
import { DesktopOnly, MobileOnly } from "./BreakpointVisibility";
import CloseButton from "./CloseButton";
import { FilterStackProps } from "components/FilterStack";
import { CustomDateInput } from "./inputs/DatePickerInput";

export const ToggleFilter = ({
  label,
  onChange = () => {
    return;
  },
  selected,
}: ToggleOption & {
  onChange?(selected: boolean): void;
  selected?: boolean;
}): ReactElement => (
  <HStack>
    <Text textStyle="body2" fontWeight="bold">
      {label}
    </Text>
    <Spacer />
    <Switch
      onChange={(event) => onChange(event.target.checked)}
      isChecked={selected}
    />
  </HStack>
);

export const DateRangeFilter = ({
  filterKey,
  onChange = () => {
    return;
  },
  startDate,
  endDate,
}: {
  filterKey: string;
  onChange?(key: string, start: Date | undefined, end: Date | undefined): void;
  startDate?: Date;
  endDate?: Date;
}): ReactElement => {
  const FilterDateInput = ({
    value,
    onChange: _onChange,
    isSelectedStart,
  }: {
    value: Date | undefined;
    onChange(date: Date | undefined): void;
    isSelectedStart: boolean;
  }) => (
    <DatePicker
      selected={value}
      onChange={(date) => _onChange(date ?? undefined)}
      selectsStart={isSelectedStart}
      selectsEnd={!isSelectedStart}
      startDate={startDate}
      endDate={endDate}
      isClearable
      minDate={!isSelectedStart ? startDate : undefined}
      customInput={<CustomDateInput />}
      dateFormat="MM/yyyy"
      showMonthYearPicker
    />
  );

  return (
    <Stack spacing={4}>
      <Text textStyle="body2" fontWeight="black">
        Date
      </Text>
      <Stack spacing={3}>
        <HStack gap={0}>
          <Text textStyle="label2">Start</Text>
          <Spacer />
          <FilterDateInput
            value={startDate}
            onChange={(date) => onChange(filterKey, date, endDate)}
            isSelectedStart={true}
          />
        </HStack>
        <HStack gap={0}>
          <Text textStyle="label2">End</Text>
          <Spacer />
          <FilterDateInput
            value={endDate}
            onChange={(date) => onChange(filterKey, startDate, date)}
            isSelectedStart={false}
          />
        </HStack>
      </Stack>
    </Stack>
  );
};

export const CheckableFilter = ({
  label,
  checked,
  onChange,
}: SelectOption & {
  checked: boolean;
  onChange(selected: boolean): void;
}): ReactElement => (
  <HStack spacing={2}>
    <Checkbox
      isChecked={checked}
      onChange={(event) => onChange(event.target.checked)}
      data-testid="checkbox"
    />

    <Text textStyle="body2">{label}</Text>
  </HStack>
);

const filterLineHeight = "24px";

const filterSetScrollFilterKeys = ["products"];

export const FilterSet = ({
  options,
  selectedOptions = [],
  heading,
  filterKey,
  onChange = () => {
    return;
  },
}: {
  options?: SelectOption[];
  selectedOptions?: string[];
  heading: string;
  filterKey: string;
} & Pick<FilterStackProps, "onChange">): ReactElement => {
  const optionsList = useMemo(() => {
    if (options === undefined) {
      return (
        <>
          <Skeleton height={filterLineHeight} />
          <Skeleton height={filterLineHeight} />
          <Skeleton height={filterLineHeight} />
        </>
      );
    }
    if (options.length <= 0) {
      return (
        <Text color="grey.500" height={filterLineHeight}>
          No filters available.
        </Text>
      );
    }
    return options.map((option) => {
      const selectedIndex = selectedOptions.findIndex(
        (selectedOption) => selectedOption === option.value
      );
      return (
        <CheckableFilter
          {...option}
          key={option.value}
          checked={selectedIndex > -1}
          onChange={(selected: boolean) => {
            if (!selected && selectedIndex > -1) {
              onChange(filterKey, [
                ...selectedOptions.slice(0, selectedIndex),
                ...selectedOptions.slice(selectedIndex + 1),
              ]);
            } else if (selected && selectedIndex < 0) {
              onChange(filterKey, [...selectedOptions, option.value]);
            }
          }}
        />
      );
    });
  }, [filterKey, onChange, options, selectedOptions]);
  const shouldScrollOptionsListStack = useCallback(
    () => filterSetScrollFilterKeys.includes(filterKey),
    [filterKey]
  );
  return (
    <Stack spacing={4}>
      <Text textStyle="body2" fontWeight="black">
        {heading}
      </Text>
      <Stack
        spacing={2}
        overflowY={shouldScrollOptionsListStack() ? "auto" : "unset"}
        h="100%"
      >
        {optionsList}
      </Stack>
    </Stack>
  );
};

const paddingX = mobileStyleBreakpoint(6, 4);
const paddingY = mobileStyleBreakpoint(8, 6);

const FilterCard = ({
  onClose = () => {
    return;
  },
  heading = "Filter Contracts",
  children,
  clearButtonText = "Clear",
  onClear = () => {
    return;
  },
  ...rest
}: PropsWithChildren<
  {
    onClose?(): void;
    onClear?(): void;
    clearButtonText?: string;
    heading?: string;
  } & CardProps
>): ReactElement => {
  const { isUsingInitialFilters } = useRequestParams();

  return (
    <Card {...rest}>
      <DesktopOnly>
        <CardHeader px={4}>
          <HStack width="100%">
            <Text textStyle="h4" fontWeight="black">
              {heading}
            </Text>
            <Spacer flex={1} />
            {!isUsingInitialFilters && (
              <Button
                variant="link"
                color="warningRed"
                onClick={onClear}
                textStyle="ctaSmall"
              >
                {clearButtonText}
              </Button>
            )}
          </HStack>
        </CardHeader>
      </DesktopOnly>
      <MobileOnly>
        <HStack px={paddingX} mt={4} width="100%">
          <Text textStyle="h4" fontWeight="black">
            Filter Contracts
          </Text>
          <Spacer />
          {!isUsingInitialFilters && (
            <Button
              variant="link"
              color="warningRed"
              onClick={onClear}
              textStyle="ctaSmall"
            >
              {clearButtonText}
            </Button>
          )}
          <CloseButton minWidth={0} boxSize={6} onClick={onClose} />
        </HStack>
      </MobileOnly>
      <CardBody paddingX={paddingX} paddingY={paddingY} flex={1}>
        <Stack w="100%" spacing={4} flex={1}>
          {children}
          <MobileOnly>
            <Spacer flex={1} />
            <PrimaryButton w="100%" onClick={onClose}>
              Close
            </PrimaryButton>
          </MobileOnly>
        </Stack>
      </CardBody>
    </Card>
  );
};
export default FilterCard;
